
<script setup lang="ts">

import { ConditionType } from "@domain/Vehicle";
import { WorkOrderType } from "@domain/CarePlan/WorkOrders/WorkOrderType";
import { FeatureType } from "@domain/CarePlan/WorkOrders/FeatureType";
import type { IMyGarageViewModel } from "@app/MyGarage";

import FeatureTile from "./Components/FeatureTile.vue";
import TabSectionHeader from "./Components/TabSectionHeader.vue";
import Cashback from "./Components/Cashback.vue";

const model = defineModel<IMyGarageViewModel>( { required: true } );

const tyreRepair = model.value.carePlan.carbucksRewards.workOrderConfigurations.find( i => i.type == WorkOrderType.TyreRepair && i.product == FeatureType.Rewards );
const windscreen = model.value.carePlan.carbucksRewards.workOrderConfigurations.find( i => i.type == WorkOrderType.WindscreenRepair && i.product == FeatureType.Rewards );
const healthCheck = model.value.carePlan.carbucksRewards.workOrderConfigurations.find( i => i.type == WorkOrderType.VehicleHealthChecks && i.product == FeatureType.Rewards );

const showTyreRepair = tyreRepair != null && model.value.carePlan.carbucksProtect.isEnabled == false;
const showWindscreenRepair = windscreen != null;
const showHealthChecks = healthCheck != null;
const showConcierge = true;
const displayAsProduct = model.value.carePlan.carbucksRewards.showAsProductInApp;

const slotsUsed = () => {
    var count = 0; // Concierge
    if( showConcierge ) count++;
    if( showTyreRepair ) count++;
    if( showHealthChecks ) count++;
    if( showWindscreenRepair ) count++;
    return count;
}

const slotsRemaining = 3 - ( slotsUsed() % 3 );

</script>

<template>

    <div class="bg-gray-850">

        <TabSectionHeader
            v-if="displayAsProduct"
            more="/my-garage/care-plan/carbucks-rewards">
            <img 
                src="@/assets/images/rewards.png"  
                class="h-6 3xl:h-8" 
            />
            <div class="text-white/80 text-xs 2xl:text-sm pt-2">
                From Suttons Automotive Arncliffe
            </div>
        </TabSectionHeader>

        <div :class="displayAsProduct ? 'bg-gradient-to-b from-gray-700 to-gray-850 via-50% via-gray-800 pt-2' : '-mt-3'">

            <div class="grid grid-cols-3 gap-4 2xl:gap-6 px-4 2xl:px-6">
        
                <h1 v-if="displayAsProduct" 
                    class="col-span-3 text-base 2xl:text-xl text-center pb-2 text-white/30">
                    Call the Concierge to organise your<br/>next service or repair
                </h1>
                
                <FeatureTile 
                    v-if="showTyreRepair"
                    :to="`/my-garage/care-plan/item/${WorkOrderType.TyreRepair}/${ConditionType.Tyres}`"
                    icon="help-me:flat-tyre" 
                    :work-order-condition="tyreRepair">
                    Puncture<br/>Repair
                </FeatureTile>

                <FeatureTile 
                    v-if="showWindscreenRepair" 
                    icon="help-me:windscreen" 
                    :to="`/my-garage/care-plan/item/${WorkOrderType.WindscreenRepair}`"
                    :work-order-condition="windscreen">
                    Chipped<br/>Windscreen
                </FeatureTile>

                <FeatureTile 
                    v-if="showHealthChecks" 
                    icon="help-me:health-checks" 
                    :to="`/my-garage/care-plan/item/${WorkOrderType.VehicleHealthChecks}`"
                    :work-order-condition="healthCheck"
                    small-icon>
                    Book A<br/>Health Check
                </FeatureTile>

                <FeatureTile 
                    v-if="showConcierge"
                    to="/my-garage/concierge"
                    icon="special:concierge-grayscale"
                    small-icon>
                    Concierge
                    <template #sub-title>
                        General bookings
                    </template>
                </FeatureTile>

                <!-- CASHBACK BUTTON -->
                <Cashback 
                    v-model="model"
                    :class="slotsRemaining == 2 ? 'col-span-2' : 'col-span-3 min-h-24'" 
                />

            </div>

        </div>

    </div>

</template>